import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../api/client.js";
import { useMe } from "../api/items.js";
import type { AccountDto } from "../api/types.js";
import { ErrorState, Loading } from "../components/Loading.js";
import { formatRelative } from "../lib/format.js";

const STATUS_EMOJI: Record<AccountDto["status"], string> = {
  active: "✅",
  cooldown: "⏸",
  needs_relogin: "⚠",
  banned: "🚫",
};

export function SettingsPage() {
  const me = useMe();

  if (me.isLoading) return <Loading />;
  if (me.isError) return <ErrorState error={me.error} />;
  if (!me.data) return null;

  if (me.data.role === "viewer") {
    return (
      <div className="p-3">
        <div className="rounded-2xl bg-bg-secondary p-6 text-center text-hint">
          Раздел доступен только админам и владельцу
        </div>
      </div>
    );
  }

  return <SettingsAuthorized />;
}

interface AccountLoadDto {
  accountId: string;
  login: string;
  last24h: number;
  last7d: number;
  last30d: number;
}

function SettingsAuthorized() {
  const accounts = useQuery({
    queryKey: ["accounts"],
    queryFn: () => api<{ accounts: AccountDto[] }>("/api/accounts"),
    select: (r) => r.accounts,
  });
  const load = useQuery({
    queryKey: ["accounts-load"],
    queryFn: () => api<{ load: AccountLoadDto[] }>("/api/accounts/load"),
    select: (r) => r.load,
  });

  const loadByAccount = new Map<string, AccountLoadDto>();
  load.data?.forEach((l) => loadByAccount.set(l.accountId, l));

  return (
    <div className="space-y-4 p-3">
      <h1 className="text-lg font-bold">Настройки</h1>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-hint">
          Steam-аккаунты
        </h2>
        {accounts.isLoading && <Loading />}
        {accounts.isError && <ErrorState error={accounts.error} />}
        {accounts.isSuccess && accounts.data.length === 0 && (
          <div className="rounded-lg bg-bg-secondary p-4 text-center text-sm text-hint">
            Нет аккаунтов. Добавь через POST /api/accounts (owner-only).
          </div>
        )}
        {accounts.data?.map((a) => {
          const l = loadByAccount.get(a.id);
          return (
            <div key={a.id} className="rounded-xl bg-bg-secondary p-3">
              <div className="flex items-center justify-between">
                <span className="font-medium">
                  {STATUS_EMOJI[a.status]} {a.login}
                </span>
                <span className="text-xs text-hint">{a.status}</span>
              </div>
              <div className="mt-1 grid grid-cols-2 gap-1 text-xs text-hint">
                <div>сегодня: {a.dailyRequestCount}</div>
                <div>всего: {a.totalRequests}</div>
                <div>последний логин: {formatRelative(a.lastLoginAt)}</div>
                <div>cooldown до: {formatRelative(a.cooldownUntil)}</div>
              </div>
              {l && (
                <div className="mt-2 grid grid-cols-3 gap-2 border-t border-bg pt-2 text-xs">
                  <LoadCell label="24ч" value={l.last24h} />
                  <LoadCell
                    label="7д (avg/день)"
                    value={`${l.last7d} / ${(l.last7d / 7).toFixed(1)}`}
                  />
                  <LoadCell
                    label="30д (avg/нед)"
                    value={`${l.last30d} / ${((l.last30d / 30) * 7).toFixed(1)}`}
                  />
                </div>
              )}
            </div>
          );
        })}

        {load.data && load.data.length > 0 && (
          <AggregateLoad rows={load.data} />
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-hint">
          Диагностика
        </h2>
        <Link
          to="/failures"
          className="flex items-center justify-between rounded-xl bg-bg-secondary p-3 transition active:scale-[0.99]"
        >
          <div>
            <div className="font-medium">Последние фейлы</div>
            <div className="text-xs text-hint">
              Сводка ошибок за 6ч/24ч/3д/7д с message и retry-статусом
            </div>
          </div>
          <span className="text-hint">›</span>
        </Link>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-hint">
          Расписания
        </h2>
        <Link
          to="/schedules"
          className="flex items-center justify-between rounded-xl bg-bg-secondary p-3 transition active:scale-[0.99]"
        >
          <div>
            <div className="font-medium">Расписания по тегам</div>
            <div className="text-xs text-hint">
              Задать cron на тег — все items унаследуют
            </div>
          </div>
          <span className="text-hint">›</span>
        </Link>
        <div className="rounded-lg bg-bg-secondary p-3 text-xs text-hint">
          Глобальный default cron — ключ{" "}
          <code className="text-text">schedule.default_cron</code> в /api/settings.
          Дефолт: <code className="text-text">0 4 * * *</code>.
        </div>
      </section>
    </div>
  );
}

function LoadCell({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg bg-bg p-1.5 text-center">
      <div className="text-[10px] uppercase tracking-wide text-hint">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

function AggregateLoad({ rows }: { rows: AccountLoadDto[] }) {
  const total24 = rows.reduce((s, r) => s + r.last24h, 0);
  const total7 = rows.reduce((s, r) => s + r.last7d, 0);
  const total30 = rows.reduce((s, r) => s + r.last30d, 0);
  const n = rows.length || 1;
  return (
    <div className="rounded-xl border border-bg-secondary bg-bg p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-hint">
        Средняя нагрузка на аккаунт
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <LoadCell label="день" value={(total24 / n).toFixed(1)} />
        <LoadCell label="неделя" value={(total7 / n).toFixed(0)} />
        <LoadCell label="месяц" value={(total30 / n).toFixed(0)} />
      </div>
      <div className="mt-2 text-[11px] text-hint">
        {rows.length} аккаунт{rows.length === 1 ? "" : "ов"} · всего {total30} запросов за 30д
      </div>
    </div>
  );
}
